import { pageSlugFromMarkdown } from './itemImages';
import { parentDirOfMarkdown } from './markdownPaths';

/** Repo folder for video belonging to `database/foo/bar.md` → `database/foo/bar/_video/ */
export function videoDirForMarkdown(mdPath: string): string {
  return mdPath.replace(/\.md$/i, '/_video');
}

export function videoDirsForPageSlug(parentDir: string, slug: string): string[] {
  const variants = new Set<string>([slug]);
  const underscored = slug.replace(/-/g, '_');
  const hyphenated = slug.replace(/_/g, '-');
  variants.add(underscored);
  variants.add(hyphenated);
  return [...variants].map(s => `${parentDir}/${s}/_video`);
}

export function videoRepoPath(mdPath: string, filename: string): string {
  return `${videoDirForMarkdown(mdPath)}/${filename}`;
}

export function alternateVideoRepoPaths(repoPath: string, mdPath?: string): string[] {
  const paths: string[] = [repoPath];
  const m = /^(database\/[^/]+)(\/_video\/.+)$/i.exec(repoPath);
  if (m) {
    const dir = m[1]!;
    const rest = m[2]!;
    const underscored = dir.replace(/-/g, '_');
    const hyphenated = dir.replace(/_/g, '-');
    if (underscored !== dir) paths.push(`${underscored}${rest}`);
    if (hyphenated !== dir) paths.push(`${hyphenated}${rest}`);
  }

  if (mdPath) {
    const filename = repoPath.split('/').pop();
    if (filename) {
      const parent = parentDirOfMarkdown(mdPath);
      const slug = pageSlugFromMarkdown(mdPath);
      for (const dir of videoDirsForPageSlug(parent, slug)) {
        paths.push(`${dir}/${filename}`);
      }
    }
  }

  return [...new Set(paths)];
}

const ALLOWED_EXT = new Set(['mp4', 'webm', 'mov', 'm4v', 'ogv', 'mkv']);

function randomVideoId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function newVideoFilename(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase() ?? '';
  let ext = ALLOWED_EXT.has(fromName) ? fromName : '';
  if (!ext && file.type) {
    if (file.type.includes('mp4')) ext = 'mp4';
    else if (file.type.includes('webm')) ext = 'webm';
    else if (file.type.includes('quicktime')) ext = 'mov';
    else if (file.type.includes('ogg')) ext = 'ogv';
  }
  if (!ext) ext = 'mp4';
  return `${randomVideoId()}.${ext}`;
}

export function mimeForVideoPath(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'mp4':
    case 'm4v':
      return 'video/mp4';
    case 'webm':
      return 'video/webm';
    case 'mov':
      return 'video/quicktime';
    case 'ogv':
      return 'video/ogg';
    case 'mkv':
      return 'video/x-matroska';
    default:
      return 'application/octet-stream';
  }
}

export function videoDisplayLabel(filename: string): string {
  return filename.length > 28 ? `${filename.slice(0, 25)}…` : filename;
}
